'use client';

import { useWebStorageStore } from '@/globals/webStorage/useWebStorage';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import React from 'react';
import { copyAnchorCSS } from './CopyAnchor.css';
import { store } from '@/sections/editorWidget/EditorWidget.client.loader';
import { copyLinkIcon } from '@/uiIcons/copyLinkIcon.css';
import { icon } from '@/uiPrimitives/media/icon';
import { checkBulletIcon } from '@/uiIcons/checkBulletIcon.css';
import { errorCircleIcon } from '@/uiIcons/errorCircleIcon.css';
import { hasCmsPreview } from '@/globals/config/hasCmsPreview.config';

export const CopyAnchor = ({ id }: { id: string }) => {
	const [copied, setCopied] = React.useState<'idle' | 'fulfilled' | 'rejected'>(
		'idle',
	);
	const [opened] = useWebStorageStore(store, hasCmsPreview);

	const handleCopy = async (e: React.MouseEvent | React.KeyboardEvent) => {
		e.preventDefault();

		try {
			await navigator.clipboard.writeText(id);
			setCopied('fulfilled');
		} catch (error) {
			console.error('Failed to copy anchor link:', error);
			setCopied('rejected');
		}
	};

	React.useEffect(() => {
		const abortController = new AbortController();
		document.addEventListener(
			'click',
			() => {
				setCopied('idle');
			},
			{ signal: abortController.signal },
		);
		return () => {
			abortController.abort();
		};
	}, []);

	return opened ? (
		<DOPLink
			href={`#${id}`}
			tabIndex={0}
			aria-label="Copy anchor link"
			onClick={handleCopy}
			data-link-type="copy-anchor-link"
			className={copyAnchorCSS}
		>
			{copied === 'fulfilled' && (
				<i
					className={icon({
						icon: checkBulletIcon,
						fill: 'positiveGreen',
					})}
				/>
			)}
			{copied === 'idle' && (
				<i
					className={icon({
						icon: copyLinkIcon,
						fill: 'positiveMuted',
					})}
				/>
			)}
			{copied === 'rejected' && (
				<i
					className={icon({
						icon: errorCircleIcon,
						fill: 'positiveRed',
					})}
				/>
			)}
		</DOPLink>
	) : null;
};
